<template>
  <b-container fluid
               class="esg-base-list-container">
    <!-- Card -->
    <esg-base-card :title="header"
                   :class="{ 'variable-height': variableHeight }">
      <template #actions>
        <slot name="actions"></slot>
      </template>

      <!-- Spinner -->
      <loading :active="loading"
               :z-index="1000"
               :is-full-page="false"/>
      <!-- /Spinner -->

      <!-- Filter -->
      <esg-base-filter class="esg-base-list-filter"
                       v-if="filter"
                       v-show="!loading"
                       :metadata="meta"
                       :size="size"
                       @update:filters="updateFilters"
                       :loading.sync="filterLoading"/>
      <!-- /Filter -->

      <!-- List -->
      <esg-table-with-meta ref="table"
                           v-show="!loading"
                           :endpoint="endpoint"
                           :metadata="meta"
                           :filters="filters"
                           :page-info="pageInfo"
                           :selectable="selectable"
                           :clickable="clickable"
                           @update:total="updateTotal"
                           @rowClicked="onRowClick"
                           @selected="onSelect"
                           :selectedItems="selectedItems"
                           @initialized="onInitialized"
                           :loading.sync="tableLoading"
                           max-height="100%"
                           :size="size"
                           class="my-3">
        <template #colgroup>
          <slot name="colgroup"></slot>
        </template>
        <template v-for="field in meta"
                  v-slot:[slotname(field)]="{ value, item, items, field, row, col }">
          <slot :name="slotname(field)"
                :value="value"
                :item="item"
                :items="items"
                :field="field"
                :row="row"
                :col="col"></slot>
        </template>
      </esg-table-with-meta>
      <!-- /List -->

      <!-- Pagination -->
      <template #footer>
        <esg-base-pagination :page-info="pageInfo"
                             @update:currPage="updateCurrPage"/>
      </template>
      <!-- /Pagination -->

    </esg-base-card>
  </b-container>
</template>

<script>
import EsgBaseFilter from "@/components/esg/base/EsgBaseFilter.vue";
import EsgBaseCard from "@/components/esg/base/EsgBaseCard.vue";
import EsgBasePagination from "@/components/esg/base/EsgBasePagination.vue";
import EsgTableWithMeta from "@/components/esg/base/EsgTableWithMeta.vue";

export default {
  name: "EsgBaseList",
  components: { EsgTableWithMeta, EsgBaseCard, EsgBaseFilter, EsgBasePagination },
  props: {
    // 테이블명
    name: {
      type: String,
      required: false,
    },
    // 데이터를 가져올 리소스명
    resource: {
      type: String,
      required: false,
    },
    // 데이터를 프런트 엔드에서 보여주는 데 필요한 메타정보
    metadata: {
      type: Array,
      required: false,
    },
    // 페이지 제목
    title: {
      type: String,
      required: false,
    },
    fixedFilter: {
      type: Array,
      default() {
        return [];
      }
    },
    filter: {
      type: Boolean,
      default: true
    },
    selectable: {
      type: Boolean,
      default: false
    },
    clickable: {
      type: Boolean,
      default: false
    },
    variableHeight: {
      type: Boolean,
      default: false,
    },
    size: {
      type: String,
      default: 'md'
    },
    selectedItems: {
      type: Array,
      required: false
    }
  },
  watch: {
    metaLoading() {
      this.setLoading();
    },
    filterLoading() {
      this.setLoading();
    },
    tableLoading() {
      this.setLoading();
    }
  },
  data() {
    return {
      loading: false,
      metaLoading: false,
      filterLoading: false,
      tableLoading: false,
      meta: [],
      header: null,
      endpoint: '',
      fields: [],
      filters: [],
      pageInfo: {
        curr: 1,
        total: 0,
        perPage: 10,
      },
      tableInfo: {
        table: '',
        resource: '',
        label: '',
        meta: [],
      },
    }
  },
  async created() {
    await this.initialize();
  },
  methods: {
    async initialize() {
      try {
        this.loading = true;

        // Fetch table information
        if (this.name) {
          let response = await axios.get(`/esg/lca/table-info/${this.name}`, {
            headers: { 'Authorization': accessToken() },
          });
          if (!response.data) throw new Error('올바르지 않은 테이블명입니다.');

          this.tableInfo = response.data;

          // Initialize header, meta, and endpoint using the table information
          this.header = this.tableInfo.label || null;
          this.meta = this.tableInfo.meta ? JSON.parse(this.tableInfo.meta) : null;
          this.endpoint = this.tableInfo.resource ? `esg/${this.tableInfo.resource}` : `esg/lca/tables/${this.tableInfo.table}`;
        }

        // Set header
        this.title && (this.header = this.title);

        // Set metadata
        this.metadata && (this.meta = structuredClone(this.metadata));

        // Set resource endpoint
        this.resource && (this.endpoint = this.resource);

        // Set filter
        this.filters = [...this.fixedFilter];

        this.loading = false;
      } catch (error) {
        this.loading = false;
        await this.showError(error);
      }
    },
    updateFilters(filters) {
      this.filters = [...this.fixedFilter, ...structuredClone(filters)];
    },
    updateCurrPage(curr) {
      this.pageInfo.curr = curr;
    },
    updateTotal(total) {
      this.pageInfo.total = total;
    },
    setLoading() {
      this.loading = this.metaLoading || this.filterLoading || this.tableLoading;
    },
    onRowClick(item, index) {
      this.$emit('rowClicked', item, index);
    },
    onSelect(item, items, index) {
      this.$emit('selected', item, items, index);
    },
    onInitialized(fields) {
      this.fields = fields;
    },
    slotname(field) {
      return `cell(${field.name})`;
    }
  },
}
</script>

<style scoped>
.esg-base-list-container {
  display: flex;
  flex-direction: column;
  padding-bottom: .75rem;
}

>>> .card.esg-base-card {
  /** max(View port height - top bar height - bottom padding, 500px) **/
  height: max(calc(100dvh - max(82px, 10dvh) - .75rem), 500px);
  flex-grow: 1;
  display: flex;
  flex-direction: column;
}

>>> .card.esg-base-card.variable-height {
  max-height: 75dvh;
}

>>> .card.esg-base-card .card-header {
  padding: 1rem .5rem;
}

>>> .card.esg-base-card .card-body {
  display: flex;
  flex-direction: column;
  height: 100%;
}

>>> .card.esg-base-card .card-footer {
  padding: 1.5dvh 2vw;
}
</style>